/**
 * Behavior Labels Dashboard — Doc Tam Ly Hanh Vi Co Phieu
 * 4 states: SUPPORT_DEFENSE | COILING_FOR_MARKUP | ACTIVE_MARKUP | FAKE_SUPPORT_OR_EXIT
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  Calculate,
  History,
  Psychology,
  Refresh,
  Search,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { behaviorAPI } from '../services/api';
import {
  BehaviorScoresTable,
  HeroCardsSection,
  LABEL_META,
  LoadingSkeleton,
  TickerHistoryDrawer,
  TopPerLabelSection,
  TransitionsBanner,
  WatchlistSection,
  executeBehaviorCalculation,
  exportDrawerSnapshot,
  exportWatchlistSnapshot,
  fetchTickerHistory,
  filterScores,
} from '../components/behavior-labels';

const HERO_LABELS = ['ACTIVE_MARKUP', 'COILING_FOR_MARKUP', 'SUPPORT_DEFENSE', 'FAKE_SUPPORT_OR_EXIT'];

const BehaviorLabels = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'vi' ? 'vi' : 'en';

  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [scores, setScores] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [watchlistSummary, setWatchlistSummary] = useState({ high: 0, mid: 0, low: 0 });
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [lookupTicker, setLookupTicker] = useState('');
  const [drawerTicker, setDrawerTicker] = useState(null);
  const [tickerHistory, setTickerHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [exportingWatchlistImage, setExportingWatchlistImage] = useState(false);
  const [exportingDrawer, setExportingDrawer] = useState(false);

  const watchlistExportRef = useRef(null);
  const drawerExportRef = useRef(null);
  const drawerBodyRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [summaryData, scoresData, watchlistData] = await Promise.all([
        behaviorAPI.getSummary(),
        behaviorAPI.getScores({ limit: 300 }),
        behaviorAPI.getWatchlist({ limit: 18 }),
      ]);

      setSummary(summaryData);
      setScores(scoresData.scores || []);
      setWatchlist(watchlistData.watchlist || []);
      setWatchlistSummary(watchlistData.summary || { high: 0, mid: 0, low: 0 });
    } catch (requestError) {
      setError(requestError.message || t('behavior.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleCalculate = useCallback(() => executeBehaviorCalculation({
    fetchData,
    setCalculating,
    setError,
    t,
  }), [fetchData, t]);

  const openHistory = useCallback((ticker) => fetchTickerHistory({
    ticker,
    setDrawerTicker,
    setHistoryLoading,
    setTickerHistory,
  }), []);

  const handleExportDrawerImage = useCallback(() => exportDrawerSnapshot({
    drawerExportRef,
    drawerBodyRef,
    drawerTicker,
    exportingDrawer,
    setExportingDrawer,
    tickerHistory,
  }), [drawerTicker, exportingDrawer, tickerHistory]);

  const handleExportWatchlistImage = useCallback(() => exportWatchlistSnapshot({
    watchlistExportRef,
    exportingWatchlistImage,
    watchlistLength: watchlist.length,
    summaryTradeDate: summary?.trade_date,
    setExportingWatchlistImage,
    setError,
    t,
  }), [exportingWatchlistImage, summary?.trade_date, t, watchlist.length]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return <LoadingSkeleton />;
  }

  const filtered = filterScores(scores, filter, search);
  const byLabelMap = {};
  (summary?.by_label || []).forEach((entry) => {
    byLabelMap[entry.behavior_label] = entry;
  });
  const neutralCount = byLabelMap.NEUTRAL?.count || (summary?.total || 0) - HERO_LABELS.reduce((sum, label) => sum + (byLabelMap[label]?.count || 0), 0);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 14px rgba(124, 58, 237, 0.3)',
            }}
          >
            <Psychology sx={{ fontSize: 22, color: '#fff' }} />
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                {t('behavior.pageTitle')}
              </Typography>
              {summary?.trade_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  {summary?.data_freshness && (() => {
                    const allFresh = Object.values(summary.data_freshness).every((value) => value === summary.trade_date);
                    const staleKeys = Object.entries(summary.data_freshness)
                      .filter(([, value]) => value !== summary.trade_date)
                      .map(([key, value]) => `${key}: ${value}`);

                    return (
                      <Tooltip title={allFresh ? t('behavior.transition.freshOk') : `${t('behavior.transition.freshWarn')}\n${staleKeys.join(', ')}`} arrow>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: allFresh ? '#10b981' : '#f59e0b', boxShadow: `0 0 6px ${allFresh ? '#10b981' : '#f59e0b'}` }} />
                      </Tooltip>
                    );
                  })()}
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                    {summary.trade_date}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('behavior.pageDesc')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            value={lookupTicker}
            onChange={(event) => setLookupTicker(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && lookupTicker.trim()) {
                openHistory(lookupTicker.trim());
              }
            }}
            placeholder={t('behavior.lookupPlaceholder', { defaultValue: 'Tra ma (VD: HPG)' })}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 16, color: '#94a3b8' }} />
                  </InputAdornment>
                ),
                endAdornment: lookupTicker ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => openHistory(lookupTicker.trim())} sx={{ p: 0.25, color: '#7c3aed' }}>
                      <History sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
            sx={{
              width: 180,
              '& .MuiOutlinedInput-root': {
                fontSize: '0.82rem',
                fontFamily: 'monospace',
                fontWeight: 700,
                letterSpacing: '0.04em',
                bgcolor: '#fff',
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
              },
              '& input::placeholder': { fontFamily: 'inherit', fontWeight: 400, letterSpacing: 'normal' },
            }}
          />

          <Button
            variant="contained"
            startIcon={calculating ? <CircularProgress size={16} color="inherit" /> : <Calculate />}
            onClick={handleCalculate}
            disabled={calculating}
            sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px', bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}
          >
            {calculating ? t('behavior.calculating') : t('behavior.calculate')}
          </Button>

          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading}
            sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px', color: '#64748b', borderColor: '#e2e8f0', '&:hover': { bgcolor: '#f8fafc' } }}
          >
            {t('behavior.refresh')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 1.5 }}>
          <Typography sx={{ fontSize: '0.82rem', color: '#dc2626' }}>{error}</Typography>
        </Box>
      )}

      <HeroCardsSection byLabelMap={byLabelMap} heroLabels={HERO_LABELS} t={t} />

      <WatchlistSection
        watchlistExportRef={watchlistExportRef}
        watchlist={watchlist}
        watchlistSummary={watchlistSummary}
        exportingWatchlistImage={exportingWatchlistImage}
        onExportWatchlistImage={handleExportWatchlistImage}
        t={t}
        locale={locale}
        onOpen={openHistory}
      />

      <TransitionsBanner transitions={summary?.transitions} t={t} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <ToggleButtonGroup value={filter} exclusive onChange={(_, value) => value && setFilter(value)} size="small">
          <ToggleButton value="ALL" sx={{ textTransform: 'none', fontSize: '0.72rem', fontWeight: 600, px: 1.5 }}>
            {t('behavior.filterAll')} ({summary?.total || 0})
          </ToggleButton>
          {HERO_LABELS.map((label) => {
            const meta = LABEL_META[label];
            const count = byLabelMap[label]?.count || 0;

            return (
              <ToggleButton
                key={label}
                value={label}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  px: 1.5,
                  '&.Mui-selected': { bgcolor: alpha(meta.color, 0.1), color: meta.color },
                }}
              >
                {t(`behavior.labels.${label}.short`)} ({count})
              </ToggleButton>
            );
          })}
          <ToggleButton
            value="NEUTRAL"
            sx={{ textTransform: 'none', fontSize: '0.72rem', fontWeight: 600, px: 1.5, '&.Mui-selected': { bgcolor: alpha('#94a3b8', 0.1), color: '#64748b' } }}
          >
            {t('behavior.labels.NEUTRAL.short')} ({neutralCount})
          </ToggleButton>
        </ToggleButtonGroup>

        <TextField
          size="small"
          placeholder={t('behavior.searchPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 18, color: '#94a3b8' }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: 200, '& .MuiOutlinedInput-root': { fontSize: '0.82rem', borderRadius: 1 } }}
        />
      </Box>

      <BehaviorScoresTable filtered={filtered} locale={locale} onOpen={openHistory} t={t} />

      <TopPerLabelSection heroLabels={HERO_LABELS} summary={summary} t={t} />

      <TickerHistoryDrawer
        drawerBodyRef={drawerBodyRef}
        drawerExportRef={drawerExportRef}
        drawerTicker={drawerTicker}
        exportingDrawer={exportingDrawer}
        historyLoading={historyLoading}
        language={locale}
        onClose={() => setDrawerTicker(null)}
        onExport={handleExportDrawerImage}
        t={t}
        tickerHistory={tickerHistory}
      />
    </Box>
  );
};

export default BehaviorLabels;